/**
 * Centrifugo WebSocket hooks for real-time messaging
 * Provides connection management and subscription handling
 */

import { type Subscription } from 'centrifuge';
import { useEffect, useRef, useCallback, useContext } from 'react';
import { CentrifugoContext } from '../contexts/CentrifugoContext';
import { subscribeChannel, unsubscribeChannel } from '@resida/realtime-client';
import type { ChatMessage } from '../lib/api';

export interface ReadReceipt {
  participantId: string;
  identityId: string;
  lastReadMessageId: string;
  readAt: string;
}

/**
 * Hook to access the shared Centrifugo connection from CentrifugoProvider.
 * Falls back to { client: null, isConnected: false } when no provider is present
 * (e.g. in isolated tests), so existing callers continue to work unchanged.
 */
export function useCentrifugo() {
  const ctx = useContext(CentrifugoContext);
  return {
    client: ctx.client,
    isConnected: ctx.isConnected,
  };
}

/**
 * Hook to subscribe to a conversation's real-time updates
 * Handles new messages and read receipts
 */
export function useConversationSubscription(
  conversationId: string | null,
  onMessage: (message: ChatMessage) => void,
  onReadReceipt: (receipt: ReadReceipt) => void,
) {
  const { client } = useCentrifugo();
  const subscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    if (!client || !conversationId) return;

    // Clean up any existing subscription first
    unsubscribeChannel(client, subscriptionRef.current);
    subscriptionRef.current = null;

    const channel = `chat:conversation:${conversationId}`;
    const sub = subscribeChannel(client, channel);

    sub.on('publication', (ctx) => {
      const { type, ...data } = ctx.data;
      if (type === 'message') {
        onMessage(data.message);
      } else if (type === 'read_receipt') {
        onReadReceipt(data.readReceipt);
      }
    });

    sub.on('error', (ctx) => {
      // Subscription failures degrade real-time updates to polling/manual refresh;
      // HTTP-based chat still works. Warn rather than error to avoid noise.
      console.warn('Subscription error:', ctx);
    });

    sub.subscribe();
    subscriptionRef.current = sub;

    return () => {
      unsubscribeChannel(client, subscriptionRef.current);
      subscriptionRef.current = null;
    };
  }, [client, conversationId, onMessage, onReadReceipt]);
}

/**
 * Hook to subscribe with stable callbacks
 * Useful when you want to update state in response to messages
 */
export function useConversationSubscriptionWithCallbacks(
  conversationId: string | null,
  callbacks: {
    onMessage?: (message: ChatMessage) => void;
    onReadReceipt?: (receipt: ReadReceipt) => void;
  }
) {
  // Extract callbacks to avoid dependency on the callbacks object itself
  const { onMessage: onMessageCallback, onReadReceipt: onReadReceiptCallback } = callbacks;

  const onMessage = useCallback((message: ChatMessage) => {
    onMessageCallback?.(message);
  }, [onMessageCallback]);

  const onReadReceipt = useCallback((receipt: ReadReceipt) => {
    onReadReceiptCallback?.(receipt);
  }, [onReadReceiptCallback]);

  useConversationSubscription(conversationId, onMessage, onReadReceipt);
}

// The repair thread message shape has a single source: @resida/api-types/repair.
export type { RepairMessage } from '../api/repairs.api';

/**
 * Hook to subscribe to a walkthrough's condition-photo approvals
 * (condition-photos:<walkthroughId>). Fires `onApproved(photoId)` when the out-of-repo
 * normalizer finishes a photo (PENDING -> APPROVED), so the runner page can swap its
 * "Processing…" placeholder for the now browser-renderable normalized thumbnail without a
 * reload. The event is URL-less (NFR-SECURITY-6) — the page re-fetches through the
 * authz-gated download proxy. No-ops when no shared client is present (e.g. in tests).
 */
export function useConditionPhotoApprovals(
  walkthroughId: string | null,
  onApproved: (photoId: string) => void
) {
  const { client } = useCentrifugo();
  const subscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    if (!client || !walkthroughId) return;

    unsubscribeChannel(client, subscriptionRef.current);
    subscriptionRef.current = null;

    const channel = `condition-photos:${walkthroughId}`;
    const sub = subscribeChannel(client, channel);

    sub.on('publication', (ctx) => {
      const data = ctx.data as { type?: string; photoId?: string };
      if (data.type === 'photo.approved' && data.photoId) {
        onApproved(data.photoId);
      }
    });

    sub.on('error', (ctx) => {
      // Degrades to the existing re-open recovery; the placeholder still resolves manually.
      console.warn('Condition photo subscription error:', ctx);
    });

    sub.subscribe();
    subscriptionRef.current = sub;

    return () => {
      unsubscribeChannel(client, subscriptionRef.current);
      subscriptionRef.current = null;
    };
  }, [client, walkthroughId, onApproved]);
}

export interface SupportMessage {
  id: string;
  senderRole: 'USER' | 'OPERATOR' | 'SYSTEM';
  content: string;
  createdAt: string;
}

/**
 * Hook to subscribe to a support conversation channel (support:conversation:<id>)
 * Handles real-time support messages
 */
export function useSupportConversationSubscription(
  conversationId: string | null,
  onMessage: (message: SupportMessage) => void
) {
  const { client } = useCentrifugo();
  const subscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    if (!client || !conversationId) return;

    unsubscribeChannel(client, subscriptionRef.current);
    subscriptionRef.current = null;

    const channel = `support:conversation:${conversationId}`;
    const sub = subscribeChannel(client, channel);

    sub.on('publication', (ctx) => {
      const { type, ...data } = ctx.data;
      if (type === 'message') {
        onMessage(data.message);
      }
    });

    sub.on('error', (ctx) => {
      console.warn('Support subscription error:', ctx);
    });

    sub.subscribe();
    subscriptionRef.current = sub;

    return () => {
      unsubscribeChannel(client, subscriptionRef.current);
      subscriptionRef.current = null;
    };
  }, [client, conversationId, onMessage]);
}
